import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Chip } from '@mui/material';
import axios from 'axios';

function formatGameDate(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = d - now;
  if (Math.abs(diff) < 3600000 * 3) return 'Live';
  if (diff > 0) {
    const days = Math.floor(diff / 86400000);
    if (days === 0) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
    if (days === 1) return 'Tomorrow';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function GameCard({ game }) {
  const comp = game.competitions?.[0];
  if (!comp) return null;
  const teams = comp.competitors || [];
  const home = teams.find(t => t.homeAway === 'home');
  const away = teams.find(t => t.homeAway === 'away');
  const status = comp.status?.type;
  const isLive = status?.state === 'in';
  const isFinal = status?.completed || status?.state === 'post';
  const statusText = status?.shortDetail || status?.description || '';

  return (
    <Box sx={{
      borderRadius: 1.5, border: '1px solid #1a2e44',
      backgroundColor: '#131318', p: 1, mb: 1,
      '&:hover': { borderColor: 'rgba(245,166,35,0.25)' },
    }}>
      {/* Away team */}
      {[away, home].map((t, i) => t ? (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: i === 0 ? 0.5 : 0 }}>
          {t.team?.abbreviation && (
            <Box component="img"
              src={`https://a.espncdn.com/i/teamlogos/nfl/500/${t.team.abbreviation.toLowerCase()}.png`}
              alt="" onError={e => { e.target.style.display = 'none'; }}
              sx={{ width: 18, height: 18, borderRadius: '2px', objectFit: 'contain', flexShrink: 0 }} />
          )}
          <Typography sx={{ fontSize: '0.75rem', fontWeight: isFinal ? (Number(t.score) > Number(i === 0 ? home?.score : away?.score) ? 700 : 400) : 500, color: '#e8f0f8', flex: 1, lineHeight: 1.2 }}>
            {t.team?.abbreviation}
          </Typography>
          {(isLive || isFinal) && (
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#e8f0f8', fontVariantNumeric: 'tabular-nums' }}>
              {t.score || '0'}
            </Typography>
          )}
        </Box>
      ) : null)}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        {isLive && <Chip label="LIVE" size="small" color="error" sx={{ height: 14, fontSize: '0.55rem', fontWeight: 800 }} />}
        <Typography sx={{ fontSize: '0.62rem', color: isLive ? '#f44336' : '#3a5070' }}>
          {isLive ? statusText : isFinal ? `Final` : formatGameDate(game.date)}
        </Typography>
      </Box>
    </Box>
  );
}

let _nflGamesCache = null;
let _cfbGamesCache = null;

export function NFLGamesPanel() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('');

  useEffect(() => {
    if (_nflGamesCache) { setGames(_nflGamesCache.games); setLabel(_nflGamesCache.label); setLoading(false); return; }
    axios.get('https://site.api.espn.com/apis/site/v2/sports/football/nfl/scoreboard', { timeout: 8000 })
      .then(r => {
        const events = r.data?.events || [];
        const week = r.data?.week?.text || '';
        const season = r.data?.season?.type;
        const isOffseason = season === 4 || (!events.length);
        const lbl = isOffseason ? 'Offseason' : week || 'This Week';
        _nflGamesCache = { games: events.slice(0, 8), label: lbl };
        setGames(events.slice(0, 8));
        setLabel(lbl);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={24} color="primary" /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#9eb8d0', letterSpacing: '0.06em' }}>
          🏈 GAMES
        </Typography>
        {label && <Chip label={label} size="small" sx={{ height: 16, fontSize: '0.58rem', bgcolor: '#1a2e44', color: '#6a8aa8' }} />}
      </Box>
      {!games.length ? (
        <Box sx={{ p: 2, borderRadius: 1.5, border: '1px dashed #1a2e44', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#3a5070', lineHeight: 1.6 }}>
            🏖️ Offseason<br />
            <span style={{ fontSize: '0.65rem' }}>Games return in August for preseason</span>
          </Typography>
        </Box>
      ) : (
        games.map(g => <GameCard key={g.id} game={g} />)
      )}
    </Box>
  );
}

export function CollegeGamesPanel() {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('');

  useEffect(() => {
    if (_cfbGamesCache) { setGames(_cfbGamesCache.games); setLabel(_cfbGamesCache.label); setLoading(false); return; }
    axios.get('https://site.api.espn.com/apis/site/v2/sports/football/college-football/scoreboard?groups=80', { timeout: 8000 })
      .then(r => {
        const events = r.data?.events || [];
        const week = r.data?.week?.text || '';
        _cfbGamesCache = { games: events.slice(0, 8), label: week || 'Recent Games' };
        setGames(events.slice(0, 8));
        setLabel(week || 'Recent Games');
        setLoading(false);
      }).catch(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress size={24} color="primary" /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#9eb8d0', letterSpacing: '0.06em' }}>
          🎓 GAMES
        </Typography>
        {label && <Chip label={label} size="small" sx={{ height: 16, fontSize: '0.58rem', bgcolor: '#1a2e44', color: '#6a8aa8' }} />}
      </Box>
      {!games.length ? (
        <Box sx={{ p: 2, borderRadius: 1.5, border: '1px dashed #1a2e44', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#3a5070', lineHeight: 1.6 }}>
            🏖️ Offseason<br />
            <span style={{ fontSize: '0.65rem' }}>Season kicks off in late August</span>
          </Typography>
        </Box>
      ) : (
        games.map(g => <GameCard key={g.id} game={g} />)
      )}
    </Box>
  );
}
